import React, {useCallback, useEffect, useState} from 'react';
import {Link, useParams} from "react-router-dom";
import {pathParams} from "path-params";
import {AppButton} from "../ui/AppButton";
import {makeStyles} from "@mui/styles";
import {useFormik} from "formik";
import {Theme} from "@mui/material";
import {useIntegrationsContext} from "../../api/integrations/IntegrationsContext";
import {showError} from "../../utils/NotificationUtils";
import {SuccessIcon} from "../icons/SuccessIcon";
import {objToFieldsArray} from "../../helpers/objectHelpers";
import {AppField} from "../ui/AppField";
import {AppAutoComplete} from "../ui/AppAutoComplete";
import {GetConnectionsListProps} from "../../api/integrations/IntegrationsDTO";
import {Controlled} from "react-codemirror2";
import ReactJson from "react-json-view";

const useStyles = makeStyles((theme: Theme) => ({
    root: {},
    content: {
        padding: 10,
        "& p": {
            marginTop: 10,
            fontSize: 13
        }
    },
    footer: {
        borderTop: "1px solid #D7D7D7",
        display: "flex",
        flexDirection: "column",
        padding: "10px",
        gap: 20,
    },
    requestStatus: {
        display: "flex",
        border: "1px solid #D7D7D7",
        borderRadius: 14,
        margin: "10px 0",
        alignItems: "center",
        padding: 15,
        "& h3": {
            marginLeft: 10
        }
    },
    testDataList: {
        maxHeight: theme.typography.pxToRem(300),
        overflow: "auto",
    },
    testData: {
        background: "#0C0D0E",
        borderRadius: "16px",
        padding: "16px",
        fontSize: "12px",
        marginTop: "10px"
    },
    testDataRow: {
        display: "flex",
        gap: "6px"
    },

}));

interface Props {
    readonly isTrigger: boolean
}

export function IntegrationEventTest({isTrigger}: Props) {
    const classes = useStyles();
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState<boolean | string>("empty");
    const [response, setResponse] = useState<any>({});
    const params: any = useParams();
    const {IntegrationsApi} = useIntegrationsContext();
    const [currentAcc, setCurrentAcc] = useState<number | null>();
    const [connections, setConnections] = useState<GetConnectionsListProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [rawValue, setRawValue] = useState("");

    const handleTest = useCallback((values) => {
        setTesting(true);
        console.log(values)
        IntegrationsApi.IntegrationEventTest(`/${isTrigger ? "trigger" : "action"}/test`, {
            raw: JSON.parse(values.raw),
            authAccountId: values.authAccountId,
            objectId: Number(isTrigger ? params.triggerId : params.actionId),
        }).then((res) => {
            setTesting(false);
            setResult(true);
            setResponse(res.payload && res.payload)
        }).catch(err => {
            showError(err);
            setTesting(false);
            setResult(false)
        })
    }, [IntegrationsApi, params, isTrigger]);

    useEffect(() => {
        IntegrationsApi.getConnectionsList({params: {integrationId: params.id}})
            .then((res) => {
                setConnections(res.payload.list);
                setCurrentAcc(res.payload.list[res.payload.list?.length - 1]?.id);
                setLoading(false);
            })
            .catch((err) => {
                showError(err);
                setLoading(false);
            });
    }, [IntegrationsApi]);

    const formik = useFormik({
        initialValues: {
            raw: "{ }",
            authAccountId: null,
        },
        onSubmit: (values) => handleTest(values),
        enableReinitialize: true
    });

    console.log(response);
    return (
        <div className={classes.root}>
            <div className={classes.content}>
                {
                    result === "empty" ? (
                        <div>
                            <h3>Test your Authentication Configuration</h3>
                            <p>Connect to your app using the above settings with a personal or testing account, and
                                Zapier
                                will
                                use
                                this connection to test API calls as you build the integration.</p>
                            <p>Finish <Link to={pathParams("/integration/editor/:id/authentication/", {id: params.id})}>configuring
                                authentication</Link> so you can test your Action</p>
                        </div>
                    ) : (
                        <>

                            {(result && response) && (
                                <>
                                    <div className={classes.requestStatus}>
                                        {result && <SuccessIcon done={true}/>}
                                        <h3>{result ? "Request Success" : "Request Failed"}</h3>
                                    </div>
                                    <h5><strong>Request Response:</strong></h5>
                                    <div className={classes.testData}>
                                        {/*@ts-ignore*/}
                                        <ReactJson theme="brewer"  src={response} displayDataTypes={false} iconStyle="circle"  />
                                    </div>
                                </>

                            )}
                        </>
                    )
                }
            </div>
            <div className={classes.footer}>
                <form onSubmit={formik.handleSubmit}>
                    <AppAutoComplete
                        fullWidth={true}
                        label="Select account"
                        name="authAccountId"
                        value={currentAcc}
                        onValueChange={(value) => formik.setFieldValue("authAccountId", Number(value))}
                        options={connections.map((item) => ({
                            label: item.name + " " + item.id,
                            value: String(item.id),
                        }))}
                        sx={{mb: 2}}
                    />
                    {/*@ts-ignore*/}
                    <Controlled
                        onBeforeChange={(editor, data, value) => {
                            formik.setFieldValue("raw", value);
                            setRawValue(value);
                        }}
                        value={rawValue}
                        options={{
                            lineWrapping: true,
                            lang: "javascript",
                            mode: "javascript",
                            theme: "dracula",
                            lineNumbers: true,
                            gutters: ["CodeMirror-lint-markers"],
                        }}
                    />
                    {/*<AppField fullWidth*/}
                    {/*          multiline*/}
                    {/*          variant="outlined"*/}
                    {/*          labelText="Raw"*/}
                    {/*          name="raw"*/}
                    {/*          id="raw"*/}
                    {/*          error={formik.touched.raw && Boolean(formik.errors.raw)}*/}
                    {/*          helperText={formik.touched.raw && formik.errors.raw}*/}
                    {/*          value={formik.values.raw}*/}
                    {/*          onChange={formik.handleChange}*/}
                    {/*          helpDesc="Enter JSON-formatted sample output data for the most commonly used fields from this Trigger to use in Zapier as sample data if users don’t test it. Learn more."/>*/}
                    <AppButton type="submit" sx={{mt: 1}} loading={testing} disabled={testing}>
                        {result === "empty" ? "Test Your Request" : "Retry Test"}
                    </AppButton>
                </form>
            </div>
        </div>
    );
}

